import React, { Fragment, useEffect, useState } from 'react';
// import { Redirect  } from 'react-router';
import { Redirect, Route, useLocation } from 'react-router-dom';
import { useMount } from 'react-use';
import { toast } from 'react-toastify';

import { apolloClient } from '../services/apollo';
import { isLoggedIn, logout } from '../services/auth';
import { pageView, trackUser } from '../services/googleAnalytics';
import Progress from './Progress';
import RoomListsContextProvider from '../contexts/roomListsContext';
import AdminRouteGuard from './AdminRouteGuard';
import { GET_CURRENT_USER } from '../graphql/GET_CURRENT_USER';

interface ILoggedInUser {
  id?: string;
  userType?: string;
  isBlocked?: boolean;
}

const PrivateRoute = ({ component: Component, ...rest }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState<ILoggedInUser>({});
  const [isOnboarded, setIsOnBoarded] = useState<boolean>(false);
  const [userType, setUserType] = useState<string>('CREATOR');
  const location = useLocation();

  useMount(async () => {
    setIsLoading(true);
    const auth: any = await isLoggedIn(apolloClient);
    if (auth && auth.user && auth.user.me) {
      if (auth.user.me.isBlocked) {
        toast.error('Your account has been blocked. Please contact the administrator.', {
          position: 'top-center',
          autoClose: 5000
        });
        logout();
        return;
      }

      setLoggedInUser(auth.user.me);
      setIsAuthenticated(true);
      setIsOnBoarded(auth.user.me.isOnboarded);
      setUserType(auth.user.me.userType || 'CREATOR');
      setIsLoading(false);
      return;
    }
    setIsAuthenticated(false);
    return setIsLoading(false);
  });

  useEffect(() => {
    const checkBlockedStatus = async () => {
      if (loggedInUser && loggedInUser.id) {
        try {
          const auth: any = await apolloClient.query({
            query: GET_CURRENT_USER,
            fetchPolicy: 'network-only' // Always fetch fresh data from server
          });
          if (auth && auth.data && auth.data.me && auth.data.me.isBlocked) {
            logout('Your account has been blocked. Please contact the administrator.');
            return;
          }
        } catch (error) {
          console.error('Error checking blocked status:', error);
        }
        trackUser(loggedInUser.id);
        pageView(location.pathname);
      }
    };
    checkBlockedStatus();
  }, [location, loggedInUser]);

  const getComponentOrRedirect = (props) => {
    if (!isAuthenticated) {
      return <Redirect to="/landing" />;
    }

    // Redirect to appropriate onboarding page based on user type
    if (!isOnboarded) {
      const onboardingUrl = userType === 'BRAND' ? '/brand-onboarding' : '/onboarding';
      if (props.match.url !== onboardingUrl) {
        return <Redirect to={onboardingUrl} />;
      }
    }

    return (
      <AdminRouteGuard user={loggedInUser}>
        <Component {...props} user={loggedInUser} />
      </AdminRouteGuard>
    );
  };

  return (
    <Fragment>
      <RoomListsContextProvider>
        <Progress isAnimating={isLoading} />
        {!isLoading && (
          <Route {...rest} render={(props) => getComponentOrRedirect(props)} />
        )}
      </RoomListsContextProvider>
    </Fragment>
  );
};

export default PrivateRoute;
